import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  createCompanyUser,
  deleteCompanyUser,
  fetchAllCompanyUsers,
  sendUserInviteEmail,
  updateCompanyUser,
} from "../api/user.api";
import { UserCreateBody, UserUpdateBody } from "../api/models/user.models";
import { useTranslation } from "react-i18next";

const useUsers = ({ enableUsers, companyId, organizationId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["users", companyId, organizationId],
    queryFn: () =>
      fetchAllCompanyUsers(bearerToken, { companyId, organizationId }),
    enabled: enableUsers,
  });

  // Create new company
  const createNewUser = async (
    user: UserCreateBody & { companyId: number; organizationId?: number }
  ) => {
    const result = await createUserMutation.mutateAsync(user);
    return result;
  };

  const createUserMutation = useMutation({
    mutationFn: (payload: UserCreateBody) =>
      createCompanyUser(bearerToken as string, { ...payload, companyId }),
    onError: (error) => {
      console.error("Failed to create user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-created"),
        life: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["users", companyId] });
    },
  });

  const updateUser = async (company: UserUpdateBody) => {
    const result = await adminUpdateUserMutation.mutateAsync(company);
    return result;
  };

  const adminUpdateUserMutation = useMutation({
    mutationFn: (payload: UserUpdateBody) =>
      updateCompanyUser(bearerToken as string, { ...payload, companyId }),
    onError: (error) => {
      console.error("Failed to update user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-updated"),
        life: 3000,
      });
    },
  });

  const deleteUser = async (userId: number) => {
    const result = await deleteUserMutation.mutateAsync(userId);
    return result;
  };

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) =>
      // Assuming there's a deleteCompanyUser function in the API
      deleteCompanyUser(bearerToken as string, { userId, companyId }),
    onError: (error) => {
      console.error("Failed to delete user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-delete-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", companyId] });
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-deleted"),
        life: 3000,
      });
    },
  });

  // Send user invite email
  const sendUserInvite = async ({
    userId,
    companyId,
    organizationId,
  }: {
    userId: number;
    companyId: number;
    organizationId?: number;
  }) => {
    const result = await sendUserInviteMutation.mutateAsync({
      companyId,
      userId,
      organizationId,
    });
    return result;
  };

  const sendUserInviteMutation = useMutation({
    mutationFn: (payload: {
      companyId: number;
      userId: number;
      organizationId?: number;
    }) => sendUserInviteEmail(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to send user invite email:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-invite-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-user-invite-sent"),
        life: 3000,
      });
    },
  });

  const savingNewUser = createUserMutation.isPending;
  const sendingUserInvite = sendUserInviteMutation.isPending;
  const isUpdatingUser = adminUpdateUserMutation.isPending;
  const isDeletingUser = deleteUserMutation.isPending;

  return {
    users: usersData,
    usersData,
    usersError,
    usersIsLoading,
    createNewUser,
    savingNewUser,
    sendUserInvite,
    sendingUserInvite,
    updateUser,
    isUpdatingUser,
    deleteUser,
    isDeletingUser,
  };
};

export default useUsers;
